// Look-back slider — the cooking-pane affordance for scrubbing
// through tasks that have already been completed.
//
// Semantics:
//   value 0          → no past tasks revealed; the active task is the
//                      first thing below the slider.
//   value 1..N       → the most recent N completed tasks are revealed
//                      in chronological order above the active line.
//   value === max    → every completed task is revealed.
//
// Horizontal: a pill with a native range input + n/m badge.
// Vertical:   the same input rotated -90° inside a fixed-height
//             column, used for the cooking pane's side rail.
// The wall clock is unaffected — this only governs what slice of
// the schedule is currently rendered.

import { theme } from '../theme';

export type LookbackOrientation = 'horizontal' | 'vertical';

export interface LookbackSliderProps {
  /** Current revealed-count. Clamped to 0..max by the consumer. */
  value: number;
  /** Maximum revealed-count — typically `completedPast.length`. */
  max: number;
  onChange: (next: number) => void;
  /** Default 'horizontal'. */
  orientation?: LookbackOrientation;
}

export function LookbackSlider({
  value,
  max,
  onChange,
  orientation = 'horizontal',
}: LookbackSliderProps) {
  const disabled = max <= 0;
  const clamped = Math.max(0, Math.min(value, Math.max(0, max)));
  const ariaLabel = disabled
    ? 'Look-back slider — no completed tasks to reveal yet'
    : `Look-back slider — ${clamped} of ${max} past tasks revealed`;

  if (orientation === 'vertical') {
    // Fixed-height column on a side rail. The input is rotated -90°
    // so larger values reveal more past tasks as the thumb moves up.
    // The wrapper sets a 44×44 hit target so dragging on touch stays
    // thumb-friendly even though the rotated input is visually thin.
    const wrapperHeight = 200;
    return (
      <div
        role="group"
        aria-label="Look-back slider"
        data-orientation="vertical"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.5rem 0.25rem',
          background: theme.bg.raised,
          borderRadius: '12px',
          minWidth: '44px',
        }}
      >
        <span
          aria-hidden="true"
          style={{
            fontSize: '0.7rem',
            color: theme.text.muted,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            writingMode: 'vertical-rl',
            transform: 'rotate(180deg)',
          }}
        >
          Look back
        </span>
        <div
          style={{
            width: '44px',
            height: `${wrapperHeight}px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <input
            type="range"
            min={0}
            max={Math.max(0, max)}
            step={1}
            value={clamped}
            disabled={disabled}
            aria-label={ariaLabel}
            aria-valuemin={0}
            aria-valuemax={Math.max(0, max)}
            aria-valuenow={clamped}
            aria-orientation="vertical"
            onChange={(e) => onChange(parseInt(e.target.value, 10))}
            style={{
              width: `${wrapperHeight}px`,
              transform: 'rotate(-90deg)',
              cursor: disabled ? 'not-allowed' : 'pointer',
              accentColor: theme.accent.calm,
            }}
          />
        </div>
        <span
          aria-hidden="true"
          style={{
            fontVariantNumeric: 'tabular-nums',
            fontSize: '0.8rem',
            color: theme.text.muted,
          }}
        >
          {clamped}/{max}
        </span>
      </div>
    );
  }

  return (
    <div
      role="group"
      aria-label="Look-back slider"
      data-orientation="horizontal"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        background: theme.bg.raised,
        borderRadius: '999px',
        padding: '0.25rem 0.75rem',
        minHeight: '36px',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          fontSize: '0.75rem',
          color: theme.text.muted,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
        }}
      >
        Look back
      </span>
      <input
        type="range"
        min={0}
        max={Math.max(0, max)}
        step={1}
        value={clamped}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-valuemin={0}
        aria-valuemax={Math.max(0, max)}
        aria-valuenow={clamped}
        onChange={(e) => onChange(parseInt(e.target.value, 10))}
        style={{
          flex: 1,
          minWidth: '100px',
          cursor: disabled ? 'not-allowed' : 'pointer',
          accentColor: theme.accent.calm,
        }}
      />
      <span
        aria-hidden="true"
        style={{
          fontVariantNumeric: 'tabular-nums',
          fontSize: '0.85rem',
          color: theme.text.muted,
          minWidth: '2.5em',
          textAlign: 'right',
        }}
      >
        {clamped}/{max}
      </span>
    </div>
  );
}

export default LookbackSlider;
